import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import type { Session } from '@supabase/supabase-js';

import { supabase } from '@/lib/supabase';
import { onHealthRecovered, refreshHealth } from '@/lib/refreshHealth';
import * as authService from '@/services/auth.service';
import { NotFoundError, isReconnectingError } from '@/services/errors';
import * as recoveryService from '@/services/recuperarSenha.service';
import type { RecoveryAbandonment } from '@/services/recuperarSenha.service';
import type { PermissionCache } from '@/services/auth.service';
import {
  escopoNoCanal,
  normalizarCanal,
  possuiPermissaoNoCanal,
  temVisualizarEmOutroCanal as temVisualizarEmOutroCanalNoCache,
} from '@/features/permissions/canal';
import type { Json } from '@joshua/types/database.generated';
import {
  DEFAULT_PESSOA_CONFIG,
  type AuthContextPessoa,
  type AuthContextType,
  type AuthPermission,
  type AuthUser,
  type LoadingStage,
  type PessoaConfig,
  type PessoaConfigHomeBg,
  type PessoaConfigNotificacoes,
} from '@/types/auth';

// Re-export for backward compatibility with existing jsx consumers that
// reach into AuthContext for these names.
export {
  DEFAULT_PESSOA_CONFIG,
  type AuthContextPessoa,
  type AuthContextType,
  type AuthPermission,
  type AuthUser,
  type LoadingStage,
  type PessoaConfig,
  type PessoaConfigHomeBg,
  type PessoaConfigNotificacoes,
} from '@/types/auth';
export type Escopo = authService.Escopo;

// ----------------------------------------------------------------------------
// Browser-storage helpers for the permission cache (ADR-003 / ADR-004).
// These are distinct from authService: they talk to sessionStorage, not the
// database, so they live with the context that owns the cache state.
// ----------------------------------------------------------------------------

const PERMISSION_CACHE_TTL = 5 * 60 * 1000;
const PERMISSION_CACHE_KEY = 'joshua_permission_cache';
const PERMISSION_CACHE_TS_KEY = 'joshua_permission_cache_ts';

function readCachedPermissionEntries(): PermissionCache | null {
  try {
    const cached = sessionStorage.getItem(PERMISSION_CACHE_KEY);
    const ts = sessionStorage.getItem(PERMISSION_CACHE_TS_KEY);
    if (!cached || !ts) return null;
    const age = Date.now() - parseInt(ts, 10);
    if (age >= PERMISSION_CACHE_TTL * 2) return null;
    // Caches written by bundles older than JOSH-91 have no canal: normalize to 'ambos'.
    return (JSON.parse(cached) as PermissionCache).map((p) => ({
      ...p,
      canal: normalizarCanal(p.canal),
    }));
  } catch {
    return null;
  }
}

function savePermissionCacheToStorage(cache: PermissionCache, ts: number): void {
  try {
    sessionStorage.setItem(PERMISSION_CACHE_KEY, JSON.stringify(cache));
    sessionStorage.setItem(PERMISSION_CACHE_TS_KEY, ts.toString());
  } catch {
    // Storage may be unavailable (Safari private mode); non-critical.
  }
}

function clearPermissionCacheFromStorage(): void {
  try {
    sessionStorage.removeItem(PERMISSION_CACHE_KEY);
    sessionStorage.removeItem(PERMISSION_CACHE_TS_KEY);
    localStorage.removeItem(PERMISSION_CACHE_KEY);
    localStorage.removeItem(PERMISSION_CACHE_TS_KEY);
  } catch {
    // Ignore storage errors.
  }
}

function cacheToPermissions(cache: PermissionCache): AuthPermission[] {
  return cache.map((p, idx) => ({
    id: `cache-${idx}`,
    codigo: `${p.recurso}_${p.acao}`,
    nome: `${p.recurso} ${p.acao}`.toLowerCase(),
    origem: 'perfil' as const,
  }));
}

function isTransientErrorMessage(message: string): boolean {
  return (
    /timeout/i.test(message) ||
    /Failed to fetch/i.test(message) ||
    /NetworkError/i.test(message) ||
    /network/i.test(message) ||
    !navigator.onLine
  );
}

// ----------------------------------------------------------------------------

export const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [pessoa, setPessoa] = useState<AuthContextPessoa | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingStage, setLoadingStage] = useState<LoadingStage>(null);

  // Hydrate permission cache from sessionStorage so ProtectedRoute doesn't
  // briefly render children with an empty cache after a page refresh.
  const [permissionCache, setPermissionCache] = useState<PermissionCache>(
    () => readCachedPermissionEntries() ?? [],
  );
  const [cacheTimestamp, setCacheTimestamp] = useState<number>(() => {
    try {
      const ts = sessionStorage.getItem(PERMISSION_CACHE_TS_KEY);
      return ts ? parseInt(ts, 10) : 0;
    } catch {
      return 0;
    }
  });
  const [permissions, setPermissions] = useState<AuthPermission[]>(() => {
    const cached = readCachedPermissionEntries();
    return cached ? cacheToPermissions(cached) : [];
  });

  const [authError, setAuthError] = useState<string | null>(null);
  const [securityStatus, setSecurityStatus] = useState<
    'secure' | 'fallback' | 'error' | 'inconsistent'
  >('error');
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [filialId, setFilialId] = useState<string | null>(null);
  const [pessoaConfig, setPessoaConfig] = useState<PessoaConfig>(DEFAULT_PESSOA_CONFIG);
  const [homeBgPreview, setHomeBgPreview] = useState<PessoaConfigHomeBg | null>(null);
  // JOSH-89: `isHealthy` is derived from refreshHealth only; this state keeps
  // the informational fields.
  const [sessionMeta, setSessionMeta] = useState({
    lastCheck: null as Date | null,
    tokenExpiry: null as Date | null,
    autoRefreshEnabled: true,
  });
  const refreshHealthSnapshot = useSyncExternalStore(
    refreshHealth.subscribe,
    refreshHealth.getSnapshot,
  );
  const sessionHealth = useMemo(
    () => ({ ...sessionMeta, isHealthy: refreshHealthSnapshot.status === 'ok' }),
    [sessionMeta, refreshHealthSnapshot],
  );

  // JOSH-89: set when the profile setup was answered SESSION_RECONNECTING; the
  // setup then runs once more when health returns to ok.
  const setupPendingRef = useRef<boolean>(false);

  // JOSH-73 recovery state. The persisted flag (recuperarSenha.service) gates
  // every tab; the ref also gates this tab from the start of a verify until the
  // recovery ends, in case storage is unavailable.
  const [recoveryPending, setRecoveryPending] = useState<boolean>(false);
  const [recoveryNotice, setRecoveryNotice] = useState<RecoveryAbandonment | null>(null);
  const recoveryGateRef = useRef<boolean>(false);

  // Refs mirror state for use inside stale closures (effects with [] deps).
  const pessoaRef = useRef<AuthContextPessoa | null>(null);
  const permissionsRef = useRef<AuthPermission[]>([]);
  const userRef = useRef<AuthUser | null>(null);
  pessoaRef.current = pessoa;
  permissionsRef.current = permissions;
  userRef.current = user;

  // Applies a cache to both in-memory state and sessionStorage.
  const applyPermissionCache = useCallback((cache: PermissionCache) => {
    const now = Date.now();
    setPermissionCache(cache);
    setCacheTimestamp(now);
    savePermissionCacheToStorage(cache, now);
    setPermissions(cacheToPermissions(cache));
  }, []);

  const clearAuthState = useCallback(() => {
    setUser(null);
    setPessoa(null);
    setPermissions([]);
    setPermissionCache([]);
    setCacheTimestamp(0);
    clearPermissionCacheFromStorage();
    setAuthError(null);
    setSecurityStatus('error');
    setLoading(false);
    setLoadingStage(null);
    setTenantId(null);
    setFilialId(null);
    setPessoaConfig(DEFAULT_PESSOA_CONFIG);
    localStorage.removeItem('joshua_last_user_email');
  }, []);

  // --------------------------------------------------------------------------
  // Profile load — orchestrates service calls + state updates.
  // Transient errors keep the session alive with cached permissions; missing
  // pessoa forces a full logout; permission failures fail-closed.
  // --------------------------------------------------------------------------

  const loadUserProfile = useCallback(
    async (
      authUser: AuthUser,
      context: 'initial' | 'refresh' | 'statechange' = 'initial',
    ): Promise<void> => {
      setAuthError(null);
      setLoadingStage('loading-profile');
      setupPendingRef.current = false;

      // JOSH-89: the request was not run because the session is reconnecting.
      // Keep the user signed in with whatever is loaded; retry on health ok.
      const deferUntilReconnected = (stage: string): void => {
        console.warn(`[AuthContext] ${stage} deferred: session reconnecting [${context}]`);
        setupPendingRef.current = true;
      };

      try {
        if (!authUser.email || !authUser.id) {
          throw new Error('Dados de usuário incompletos');
        }
        setUser(authUser as AuthUser);

        const timeoutMs =
          context === 'statechange' || context === 'initial' ? 30000 : 15000;

        let pessoaData: AuthContextPessoa;
        try {
          pessoaData = await authService.getPessoaByUserId(authUser.id, {
            timeoutMs,
          });
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);

          if (isReconnectingError(err)) {
            deferUntilReconnected('Pessoa load');
            return;
          }

          if (err instanceof NotFoundError) {
            clearAuthState();
            await authService.signOutFromSupabase().catch(() => undefined);
            setAuthError('Dados do usuário não encontrados. Faça login novamente.');
            return;
          }

          if (isTransientErrorMessage(message)) {
            console.warn(
              `[AuthContext] Transient error loading pessoa [${context}]:`,
              message,
            );
            if (permissionsRef.current.length > 0) {
              setSecurityStatus('secure');
              return;
            }
            setAuthError('Erro de conexão ao carregar dados. Tente recarregar a página.');
            return;
          }

          console.error(`[AuthContext] Failed to load pessoa [${context}]:`, message);
          clearAuthState();
          await authService.signOutFromSupabase().catch(() => undefined);
          setAuthError('Erro crítico no carregamento do perfil. Faça login novamente.');
          return;
        }

        if (pessoaData.user_ativo === false) {
          await authService.signOutFromSupabase().catch(() => undefined);
          clearAuthState();
          setAuthError('Acesso ao sistema desativado. Entre em contato com o administrador.');
          return;
        }

        setPessoa(pessoaData as AuthContextPessoa);
        // Resolve AUTO wallpaper server-side so the client receives a concrete
        // preset key (no client-side hash, no F5 flash from undefined→pessoaId
        // seed change). Non-AUTO settings pass through unchanged. Failure is
        // tolerated — fall back to the raw config.
        const resolvedHomeBg = await authService
          .resolveHomeBg(pessoaData.id)
          .catch(() => null);
        const baseConfig = (pessoaData.config as PessoaConfig | null) ?? null;
        if (baseConfig || resolvedHomeBg) {
          setPessoaConfig({
            ...DEFAULT_PESSOA_CONFIG,
            ...(baseConfig ?? {}),
            ...(resolvedHomeBg ? { home_bg: resolvedHomeBg } : {}),
          });
        }

        const ctx = await authService.getPessoaContext(pessoaData.id);
        setTenantId(ctx.tenantId);
        setFilialId(ctx.filialId);

        setLoadingStage('loading-permissions');
        try {
          const cache = await authService.loadPermissionCache(pessoaData.id);
          if (cache.length === 0) {
            throw new Error(
              'Nenhuma permissão encontrada. Verifique se seu perfil está configurado corretamente ou contate o administrador.',
            );
          }
          applyPermissionCache(cache);
          setSecurityStatus('secure');
        } catch (permErr) {
          if (isReconnectingError(permErr)) {
            // Keep the hydrated cache; never fail closed on a pause.
            deferUntilReconnected('Permission load');
            return;
          }
          console.error('[AuthContext] Permission load failed:', permErr);
          // Fail-closed: clear everything so the user can't operate without perms.
          clearAuthState();
          await authService.signOutFromSupabase().catch(() => undefined);
          setAuthError(
            permErr instanceof Error
              ? permErr.message
              : 'Erro ao carregar permissões. Faça login novamente.',
          );
        }
      } catch (error) {
        if (isReconnectingError(error)) {
          deferUntilReconnected('Profile setup');
          return;
        }
        console.error('[AuthContext] Critical error loading profile:', error);
        clearAuthState();
        await authService.signOutFromSupabase().catch(() => undefined);
        setAuthError(
          error instanceof Error
            ? error.message
            : 'Erro crítico no carregamento do perfil. Faça login novamente.',
        );
      } finally {
        setLoading(false);
        setLoadingStage(null);
      }
    },
    [applyPermissionCache, clearAuthState],
  );

  // --------------------------------------------------------------------------
  // Auth effects — subscribe to Supabase auth events, decoupled from I/O.
  //
  // The onAuthStateChange callback MUST stay synchronous (no awaits) to avoid
  // the GoTrueClient circular-deadlock pattern. Profile loading runs in a
  // follow-up effect that reacts to user changes.
  // --------------------------------------------------------------------------

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session?.user) {
        clearAuthState();
        return;
      }

      // JOSH-73: a recovery session never opens the app. While a recovery is
      // pending (any tab) every session event is ignored until the new
      // password is saved; completeRecovery then sets the user explicitly.
      const recoveryGated = recoveryGateRef.current || recoveryService.isRecoveryPending();
      if (recoveryGated || event === 'PASSWORD_RECOVERY') {
        setLoading(false);
        setLoadingStage(null);
        if (!recoveryGated) {
          // A recovery session this app did not start (a recovery link):
          // unsupported, so drop it locally. Deferred so no Supabase call is
          // awaited inside this callback. Fails closed: if the session cannot
          // be removed, the recovery flag gates it until the cold-start cleanup.
          const linkEmail = session.user.email ?? '';
          setTimeout(() => {
            void recoveryService.signOutLocally().then((gone) => {
              if (!gone) recoveryService.setRecoveryFlag(linkEmail);
            });
          }, 0);
        }
        return;
      }

      if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
        setUser(session.user as AuthUser);
        if (session.user.email) {
          localStorage.setItem('joshua_last_user_email', session.user.email);
        }
        return;
      }

      if (event === 'TOKEN_REFRESHED') {
        setSessionMeta((prev) => ({
          ...prev,
          lastCheck: new Date(),
          tokenExpiry: session.expires_at
            ? new Date(session.expires_at * 1000)
            : null,
        }));
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [clearAuthState]);

  // JOSH-73 cold start: a flag found on mount (reload, new tab, crash) means a
  // recovery left without a new password. The code is spent: sign out locally
  // and send the person back to the request step with the email prefilled.
  useEffect(() => {
    if (!recoveryService.isRecoveryPending()) return;
    let cancelled = false;
    void recoveryService.abandonRecovery().then((abandonment) => {
      if (!cancelled && abandonment) setRecoveryNotice(abandonment);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // JOSH-73: another tab completed the recovery (flag removed, session kept).
  // No auth event reaches this tab after the flag clears, so adopt the session.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== recoveryService.RECOVERY_FLAG_KEY || e.newValue !== null) return;
      if (recoveryGateRef.current) return;
      void supabase.auth.getSession().then(({ data }) => {
        const sessionUser = data.session?.user;
        if (sessionUser?.email && !recoveryService.isRecoveryPending()) {
          setUser(sessionUser as AuthUser);
        }
      });
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // JOSH-89: a setup deferred by a reconnecting session runs once on health ok.
  // Health can flip inside an auth event (auth lock held), so the setup is
  // scheduled on a later task instead of starting in the callback.
  useEffect(() => {
    let cancelled = false;
    const unsubscribe = onHealthRecovered(() => {
      if (!setupPendingRef.current) return;
      setTimeout(() => {
        const authUser = userRef.current;
        if (cancelled || !setupPendingRef.current || !authUser) return;
        setupPendingRef.current = false;
        void loadUserProfile(authUser, 'refresh').catch((err: unknown) => {
          if (!cancelled) console.error('[AuthContext] Deferred profile setup failed:', err);
        });
      }, 0);
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [loadUserProfile]);

  useEffect(() => {
    if (!user?.id || !user?.email) return;
    if (pessoaRef.current?.email === user.email) {
      setLoading(false);
      setLoadingStage(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        await loadUserProfile(user, 'statechange');
      } catch (err) {
        if (!cancelled) {
          console.error('[AuthContext] loadUserProfile failed:', err);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.email, loadUserProfile]);

  // --------------------------------------------------------------------------
  // Permission cache TTL refresh (ADR-003). Interval-based silent refresh.
  // --------------------------------------------------------------------------

  // Reloads the permission cache without touching the global loading flag, so
  // routes guarded by ProtectedView keep their content mounted. On failure the
  // old cache stays until the next attempt.
  const silentRefreshPermissions = useCallback(async () => {
    if (!pessoa?.id) return;
    try {
      const cache = await authService.loadPermissionCache(pessoa.id);
      applyPermissionCache(cache);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.warn('[AuthContext] Silent cache refresh failed:', message);
    }
  }, [pessoa?.id, applyPermissionCache]);

  useEffect(() => {
    if (!pessoa?.id || !cacheTimestamp) return;

    const intervalId = setInterval(() => {
      const age = Date.now() - cacheTimestamp;
      if (age <= PERMISSION_CACHE_TTL) return;
      void silentRefreshPermissions();
    }, 60 * 1000);

    return () => clearInterval(intervalId);
  }, [pessoa?.id, cacheTimestamp, silentRefreshPermissions]);

  // --------------------------------------------------------------------------
  // Auth actions — each delegates I/O to authService.
  // --------------------------------------------------------------------------

  const signIn = useCallback(
    async (email: string, senha: string) => {
      try {
        setLoading(true);
        setLoadingStage('authenticating');
        setAuthError(null);

        // JOSH-73: a normal sign-in ends any pending recovery first; otherwise
        // the recovery gate would swallow the SIGNED_IN event.
        let recoveryStuck = false;
        if (recoveryGateRef.current || recoveryService.isRecoveryPending()) {
          await recoveryService.abandonRecovery();
          // Fail closed: the flag survives when the recovery session could
          // not be removed, and the gate stays shut until it is replaced.
          recoveryStuck = recoveryService.isRecoveryPending();
          recoveryGateRef.current = recoveryStuck;
          setRecoveryPending(false);
        }

        const result = await authService.signInWithPassword(email, senha);
        if (recoveryStuck) {
          // The password sign-in replaced the stuck recovery session, so the
          // gate can open; its SIGNED_IN was swallowed, so set the user here.
          recoveryService.clearRecoveryFlag();
          recoveryGateRef.current = false;
          setUser(result.user as AuthUser);
          if (result.user?.email) {
            localStorage.setItem('joshua_last_user_email', result.user.email);
          }
        }
        // Profile load triggers via onAuthStateChange → user effect.
        return { user: result.user as AuthUser, session: result.session };
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        // P3-005: user-correctable input; UI surfaces it via setAuthError().
        // Debug-only to avoid Sentry noise from wrong-password attempts.
        console.debug('[AuthContext] Login rejected:', message);
        setAuthError(message);
        throw error;
      } finally {
        setLoading(false);
        setLoadingStage(null);
      }
    },
    [],
  );

  const signOut = useCallback(async () => {
    try {
      clearAuthState();
      try {
        await authService.signOutFromSupabase();
      } catch {
        // State already cleared locally. supabase-js keeps the stored session
        // when the server call fails (offline), so remove it here (fails
        // closed; a reload must not sign the user back in).
        await recoveryService.signOutLocally();
      }
    } catch (error) {
      console.error('[AuthContext] Logout error:', error);
      clearAuthState();
      throw error;
    }
  }, [clearAuthState]);

  // --------------------------------------------------------------------------
  // JOSH-73 password recovery — I/O and messages live in recuperarSenha.service.
  // --------------------------------------------------------------------------

  const dismissRecoveryNotice = useCallback(() => setRecoveryNotice(null), []);

  const requestRecoveryCode = useCallback(
    (email: string) => recoveryService.requestRecoveryCode(email),
    [],
  );

  const verifyRecoveryCode = useCallback(async (email: string, code: string) => {
    setRecoveryNotice(null);
    recoveryGateRef.current = true;
    let ok = false;
    try {
      const result = await recoveryService.verifyRecoveryCode(email, code);
      ok = result.ok;
      setRecoveryPending(ok);
      return result;
    } finally {
      // Success keeps the gate shut until completion or abandonment; so does
      // a failure whose recovery session could not be removed (flag kept).
      recoveryGateRef.current = ok || recoveryService.isRecoveryPending();
    }
  }, []);

  const abandonRecovery = useCallback(async () => {
    const abandonment = await recoveryService.abandonRecovery();
    // Stays shut when the session could not be removed (flag kept).
    recoveryGateRef.current = recoveryService.isRecoveryPending();
    setRecoveryPending(false);
    if (abandonment) setRecoveryNotice(abandonment);
    return abandonment;
  }, []);

  const completeRecovery = useCallback(async (password: string) => {
    const email = recoveryService.readRecoveryFlag()?.email ?? '';
    // USER_UPDATED fires while the flag is still set: keep this tab gated.
    recoveryGateRef.current = true;
    const result = await recoveryService.completeRecovery(password);
    if (result.ok) {
      recoveryGateRef.current = false;
      setRecoveryPending(false);
      // No auth event follows the flag being cleared: load the user here. The
      // profile effect then runs the same setup as a normal sign-in, including
      // the user_ativo check.
      const authUser = result.user as AuthUser;
      setUser(authUser);
      if (authUser.email) {
        localStorage.setItem('joshua_last_user_email', authUser.email);
      }
    } else if (result.abandoned) {
      // Stays shut when the session could not be removed (flag kept).
      recoveryGateRef.current = recoveryService.isRecoveryPending();
      setRecoveryPending(false);
      setRecoveryNotice({ email, message: result.message });
    }
    return result;
  }, []);

  const refreshPermissions = useCallback(async () => {
    if (user) {
      await loadUserProfile(user, 'refresh');
    }
  }, [user, loadUserProfile]);

  const forceRefreshPermissions = useCallback(async () => {
    if (!pessoa?.id || !user) return;
    try {
      setLoading(true);
      const cache = await authService.loadPermissionCache(pessoa.id);
      if (cache.length === 0) {
        throw new Error('Nenhuma permissão encontrada.');
      }
      applyPermissionCache(cache);
      setSecurityStatus('secure');
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.error('[AuthContext] Force refresh permissions failed:', message);
      setSecurityStatus('error');
      setAuthError('Erro ao carregar permissões.');
    } finally {
      setLoading(false);
      setLoadingStage(null);
    }
  }, [pessoa?.id, user, applyPermissionCache]);

  const validateAuthState = useCallback(async (): Promise<boolean> => {
    try {
      const session = await authService.getCurrentSession();
      const isValid = !!session?.user;
      setSessionMeta((prev) => ({
        ...prev,
        lastCheck: new Date(),
        tokenExpiry: session?.expires_at
          ? new Date(session.expires_at * 1000)
          : null,
      }));
      return isValid;
    } catch {
      setSessionMeta((prev) => ({
        ...prev,
        lastCheck: new Date(),
      }));
      return false;
    }
  }, []);

  // --------------------------------------------------------------------------
  // Pessoa config persistence.
  // --------------------------------------------------------------------------

  const updatePessoaConfig = useCallback(
    async (newConfig: Partial<PessoaConfig>): Promise<PessoaConfig> => {
      if (!pessoa?.id) {
        throw new Error('Usuário não autenticado');
      }

      const mergedConfig: PessoaConfig = {
        ...DEFAULT_PESSOA_CONFIG,
        ...pessoaConfig,
        ...newConfig,
        home_bg: newConfig.home_bg
          ? ({ ...pessoaConfig.home_bg, ...newConfig.home_bg } as PessoaConfigHomeBg)
          : pessoaConfig.home_bg,
        notificacoes: newConfig.notificacoes
          ? ({
              ...pessoaConfig.notificacoes,
              ...newConfig.notificacoes,
            } as PessoaConfigNotificacoes)
          : pessoaConfig.notificacoes,
      };

      // Optimistic update; revert on failure.
      setPessoaConfig(mergedConfig);

      try {
        const saved = (await authService.savePessoaConfig(
          pessoa.id,
          mergedConfig,
        )) as PessoaConfig;
        // pessoa.config is jsonb in the DB; PessoaConfig is the structural
        // contract. Cross the type boundary explicitly here rather than
        // widening Pessoa to accept PessoaConfig everywhere.
        setPessoa((prev) =>
          prev ? { ...prev, config: saved as unknown as Json } : null,
        );
        return saved;
      } catch (error) {
        setPessoaConfig(pessoaConfig);
        const message = error instanceof Error ? error.message : String(error);
        console.error('[AuthContext] Error updating config:', message);
        throw error;
      }
    },
    [pessoa?.id, pessoaConfig],
  );

  // --------------------------------------------------------------------------
  // Permission checks (pure — operate on in-memory cache).
  // --------------------------------------------------------------------------

  // A view on the other channel (JOSH-91) counts as absent here.
  const hasPermission = useCallback(
    (recurso: string, acao?: string): boolean =>
      possuiPermissaoNoCanal(permissionCache, recurso, acao ?? 'view'),
    [permissionCache],
  );

  const hasAnyPermission = useCallback(
    (permissionCodes: string[]) => permissionCodes.some((code) => hasPermission(code)),
    [hasPermission],
  );

  const getPermissionScope = useCallback(
    (recurso: string, acao: string): Escopo | null =>
      escopoNoCanal(permissionCache, recurso, acao),
    [permissionCache],
  );

  const temVisualizarEmOutroCanal = useCallback(
    (recurso: string): boolean => temVisualizarEmOutroCanalNoCache(permissionCache, recurso),
    [permissionCache],
  );

  const getAuditFields = useCallback(
    () => ({
      tenant_id: tenantId || pessoa?.tenant_id || null,
      filial_id: filialId || null,
      created_by: pessoa?.id || null,
      updated_by: pessoa?.id || null,
    }),
    [tenantId, filialId, pessoa?.tenant_id, pessoa?.id],
  );

  // --------------------------------------------------------------------------
  // Context value
  // --------------------------------------------------------------------------

  const isAuthenticated = !!user;

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      pessoa,
      permissions,
      loading,
      loadingStage,
      signIn,
      signOut,
      hasPermission,
      hasAnyPermission,
      getPermissionScope,
      temVisualizarEmOutroCanal,
      isAuthenticated,
      authError,
      securityStatus,
      refreshPermissions,
      forceRefreshPermissions,
      silentRefreshPermissions,
      validateAuthState,
      sessionHealth,
      permissionCache,
      cacheTimestamp,
      tenantId,
      filialId,
      getAuditFields,
      pessoaConfig,
      updatePessoaConfig,
      homeBgPreview,
      setHomeBgPreview,
      recoveryPending,
      recoveryNotice,
      dismissRecoveryNotice,
      requestRecoveryCode,
      verifyRecoveryCode,
      completeRecovery,
      abandonRecovery,
    }),
    [
      user,
      pessoa,
      permissions,
      loading,
      loadingStage,
      signIn,
      signOut,
      hasPermission,
      hasAnyPermission,
      getPermissionScope,
      temVisualizarEmOutroCanal,
      isAuthenticated,
      authError,
      securityStatus,
      refreshPermissions,
      forceRefreshPermissions,
      silentRefreshPermissions,
      validateAuthState,
      sessionHealth,
      permissionCache,
      cacheTimestamp,
      tenantId,
      filialId,
      getAuditFields,
      pessoaConfig,
      updatePessoaConfig,
      homeBgPreview,
      recoveryPending,
      recoveryNotice,
      dismissRecoveryNotice,
      requestRecoveryCode,
      verifyRecoveryCode,
      completeRecovery,
      abandonRecovery,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
